import { PageLayout } from '@/components/layout'
import { SubscriptionCard, Subscription } from '@/components/subscription/SubscriptionCard'
import { Card, LoadingSpinner } from '@/components/common'
import { useAuth } from '@/hooks/useAuth'
import { useSubscriptions, useCancelSubscription, useReactivateSubscription } from '@/hooks/useSubscription'
import { useToast } from '@/contexts/ToastContext'

export const SubscriptionManagement = () => {
  const { user } = useAuth()
  const { showToast } = useToast()
  const { data: subscriptionsData, isLoading, error } = useSubscriptions({
    organization_id: user?.organizationId,
  })
  const cancelMutation = useCancelSubscription()
  const reactivateMutation = useReactivateSubscription()

  // Transform API data to component format
  const subscriptions: Subscription[] = subscriptionsData?.subscriptions.map((sub) => ({
    id: sub.subscription_id,
    serviceName: sub.service_name,
    planTier: sub.plan_tier,
    status: sub.status === 'cancelled' ? 'cancelled' : sub.status,
    currentPeriodStart: sub.current_period_start,
    currentPeriodEnd: sub.current_period_end,
    paymentGateway: sub.payment_gateway,
    cancelAtPeriodEnd: sub.cancel_at_period_end,
    price: {
      amount: 0, // TODO: Get from plan data
      currency: 'SAR',
      interval: 'monthly', // TODO: Determine from subscription
    },
  })) || []

  const handleCancel = async (id: string) => {
    cancelMutation.mutate(
      { id },
      {
        onSuccess: () => {
          showToast('Subscription cancelled successfully', 'success')
        },
        onError: (error: any) => {
          showToast(
            error?.response?.data?.detail || 'Failed to cancel subscription',
            'error'
          )
        },
      }
    )
  }

  const handleReactivate = async (id: string) => {
    reactivateMutation.mutate(id, {
      onSuccess: () => {
        showToast('Subscription reactivated successfully', 'success')
      },
      onError: (error: any) => {
        showToast(
          error?.response?.data?.detail || 'Failed to reactivate subscription',
          'error'
        )
      },
    })
  }

  const handleManage = (id: string) => {
    // TODO: Navigate to subscription management or open upgrade modal
    console.log('Manage subscription:', id)
  }

  const activeSubscriptions = subscriptions.filter((sub) => sub.status === 'active')
  const inactiveSubscriptions = subscriptions.filter((sub) => sub.status !== 'active')

  if (isLoading) {
    return (
      <PageLayout showHeader={true}>
        <div className="p-6 md:p-8 lg:p-10 flex items-center justify-center min-h-[400px]">
          <LoadingSpinner size="lg" />
        </div>
      </PageLayout>
    )
  }

  if (error) {
    return (
      <PageLayout showHeader={true}>
        <div className="p-6 md:p-8 lg:p-10">
          <div className="max-w-7xl mx-auto">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-600 dark:text-red-400">
                Error loading subscriptions: {error instanceof Error ? error.message : 'Unknown error'}
              </p>
            </div>
          </div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout showHeader={true}>
      <div className="p-6 md:p-8 lg:p-10">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-text-main-light dark:text-text-main-dark mb-2">
                Subscriptions
              </h1>
              <p className="text-text-sub-light dark:text-text-sub-dark">
                Manage your service subscriptions and billing
              </p>
            </div>
            <a
              href="mailto:contact-us@binder.com?subject=Service%20Subscription%20Request&body=Hello,%0D%0A%0D%0AI%20would%20like%20to%20submit%20a%20service%20request.%0D%0A%0D%0AORGANIZATION%20NAME:%0D%0ASERVICE%20REQUESTED:%0D%0AADDITIONAL%20DETAILS:%0D%0A%0D%0AThank%20you."
              className="flex h-10 items-center justify-center rounded-lg bg-primary px-5 text-sm font-bold text-white shadow-lg shadow-primary/20 transition-all hover:bg-blue-600 hover:shadow-xl hover:shadow-primary/30 active:scale-95"
            >
              Add Subscription
            </a>
          </div>

          {/* Active Subscriptions */}
          {activeSubscriptions.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-text-main-light dark:text-text-main-dark">
                Active Subscriptions
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {activeSubscriptions.map((subscription) => (
                  <SubscriptionCard
                    key={subscription.id}
                    subscription={subscription}
                    onManage={handleManage}
                    onCancel={handleCancel}
                    onReactivate={handleReactivate}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Inactive Subscriptions */}
          {inactiveSubscriptions.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-text-main-light dark:text-text-main-dark">
                Inactive Subscriptions
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {inactiveSubscriptions.map((subscription) => (
                  <SubscriptionCard
                    key={subscription.id}
                    subscription={subscription}
                    onManage={handleManage}
                    onCancel={handleCancel}
                    onReactivate={handleReactivate}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {subscriptions.length === 0 && (
            <Card className="text-center py-12">
              <span
                className="material-symbols-outlined text-6xl text-text-sub-light dark:text-text-sub-dark mb-4"
                style={{ fontSize: '64px' }}
              >
                subscriptions
              </span>
              <h3 className="text-xl font-semibold mb-2 text-text-main-light dark:text-text-main-dark">
                No Subscriptions
              </h3>
              <p className="text-text-sub-light dark:text-text-sub-dark mb-6">
                Get started by subscribing to BLIS or IZG services.
              </p>
              <a
                href="mailto:contact-us@binder.com
                ?subject=Service%20Subscription%20Request
                &body=Hello,%0D%0A%0D%0AI%20would%20like%20to%20submit%20a%20service%20request.%0D%0A%0D%0AORGANIZATION%20NAME:%0D%0ASERVICE%20REQUESTED:%0D%0AADDITIONAL%20DETAILS:%0D%0A%0D%0AThank%20you."
                className="flex h-10 items-center justify-center rounded-lg bg-primary px-5 text-sm font-bold text-white shadow-lg shadow-primary/20 transition-all hover:bg-blue-600 hover:shadow-xl hover:shadow-primary/30 active:scale-95"
              >
                Register
              </a>
            </Card>
          )}
        </div>
      </div>
    </PageLayout>
  )
}

